import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PuckColorField from '../../../puck/fields/PuckColorField.jsx';
import { usePuckRichTextStyle } from '../../../context/PuckRichTextStyleContext';
import { normalizeCssColor } from '../../../utils/proposalRichTextPalette';
import { getSelectionTextColor } from '../../../utils/richTextSelectionColor';

/**
 * Couleur du texte dans le bubble menu — même composant que le panneau
 * latéral (PuckColorField : bouton « A » teinté, popover picker + palette).
 */
export default function RichTextBubbleColorControls({
  editor,
  rememberNow,
  runWithSelection,
  onRestoreSelection,
  lockBubbleMenu,
  unlockBubbleMenu,
}) {
  const { swatches } = usePuckRichTextStyle();
  const [draftColor, setDraftColor] = useState('#228be6');
  const [selectionColor, setSelectionColor] = useState(null);

  const swatchColors = useMemo(
    () => swatches.map((s) => s.value).filter(Boolean),
    [swatches],
  );

  useEffect(() => {
    if (!editor) return undefined;

    const syncFromSelection = () => {
      setSelectionColor(getSelectionTextColor(editor));
    };

    syncFromSelection();
    editor.on('selectionUpdate', syncFromSelection);
    editor.on('transaction', syncFromSelection);
    return () => {
      editor.off('selectionUpdate', syncFromSelection);
      editor.off('transaction', syncFromSelection);
    };
  }, [editor]);

  useEffect(() => {
    if (selectionColor) setDraftColor(selectionColor);
  }, [selectionColor]);

  const applyColor = useCallback((color, { refocusEditor = true } = {}) => {
    const normalized = normalizeCssColor(color);
    if (!normalized) return;
    setDraftColor(normalized);
    rememberNow();
    runWithSelection((chain) => chain.setColor(normalized), { focus: refocusEditor });
  }, [rememberNow, runWithSelection]);

  const pickerValue = draftColor;

  return (
    <PuckColorField
      variant="bubble"
      glyph="text"
      value={pickerValue}
      onChange={applyColor}
      swatches={swatchColors}
      size="xs"
      rememberNow={rememberNow}
      lockBubbleMenu={lockBubbleMenu}
      unlockBubbleMenu={unlockBubbleMenu}
      onRestoreSelection={onRestoreSelection}
    />
  );
}
